import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, CheckCircle2, FileText, XCircle, Send } from "lucide-react";
import { toast } from "sonner";

export default function FreeGuideTab() {
  const [info, setInfo] = useState(null);
  const [title, setTitle] = useState("");
  const [cover, setCover] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/free-guide/info").then((r) => {
    setInfo(r.data); setTitle(r.data.title || ""); setCover(r.data.cover || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveMeta = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("cover", cover);
      await api.post("/admin/free-guide", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Free guide details saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/free-guide", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Free guide PDF uploaded — subscribers can now download it");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed (must be a PDF)"); }
    finally { setUploading(false); }
  };

  const sendToAll = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/free-guide/send-all");
      toast.success(`Guide emailed to ${r.data.sent} verified subscriber${r.data.sent === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="free-guide-tab">
      <div className="border border-border bg-card p-5 flex items-center gap-3">
        {info?.available ? <CheckCircle2 className="w-5 h-5 text-primary" /> : <XCircle className="w-5 h-5 text-muted-foreground" />}
        <div>
          <p className="font-display text-lg">{info?.available ? "Guide is live" : "No PDF uploaded yet"}</p>
          <p className="text-xs text-muted-foreground">
            {info?.available ? "Verified subscribers receive the download link automatically." : "Upload the PDF below to start delivering it."}
          </p>
        </div>
      </div>

      <div className="border border-border rounded-md p-5">
        <p className="font-mono-label text-[11px] text-primary mb-1">Step 1 · Upload the guide PDF</p>
        <p className="text-xs text-muted-foreground mb-3">This is what gets delivered to subscribers. Selecting a file uploads it immediately.</p>
        <label data-testid="fg-upload"
          className="flex items-center justify-center gap-2 font-mono-label text-[11px] px-4 py-6 border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 text-primary cursor-pointer transition-colors rounded-md">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          {uploading ? "Uploading…" : (info?.available ? "Replace PDF (choose file)" : "Choose PDF to upload")}
          <input type="file" accept="application/pdf" className="hidden" disabled={uploading}
            onChange={(e) => uploadPdf(e.target.files[0])} />
        </label>
        {info?.available && (
          <p className="mt-3 text-xs text-primary flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> A PDF is stored and being delivered to subscribers.</p>
        )}
      </div>

      <div className="border-t border-border pt-6 space-y-4">
        <p className="font-mono-label text-[11px] text-muted-foreground">Step 2 · Details (optional)</p>
        <div>
          <Label className="font-mono-label text-[11px]">Guide title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="fg-title" className="mt-1" />
        </div>
        <div>
          <Label className="font-mono-label text-[11px]">Cover image URL (optional)</Label>
          <Input value={cover} onChange={(e) => setCover(e.target.value)} data-testid="fg-cover" placeholder="https://…" className="mt-1" />
        </div>
        <Button onClick={saveMeta} disabled={saving} data-testid="fg-save" variant="outline" className="rounded-full font-mono-label text-xs">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save details"}
        </Button>
      </div>

      {info?.available && (
        <div className="border-t border-border pt-6">
          <Label className="font-mono-label text-[11px]">Resend to existing subscribers</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Anyone who confirmed <em>before</em> you uploaded the PDF didn't get a download link. Send it to all verified subscribers now.
          </p>
          <Button variant="outline" onClick={sendToAll} disabled={sending} data-testid="fg-send-all"
            className="rounded-full font-mono-label text-[11px]">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
            Email guide to all verified subscribers
          </Button>
        </div>
      )}
    </div>
  );
}
